import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { CssBaseline } from '@mui/material';
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
} from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Login } from '@pages/Login/Login.tsx';
import Root from '@pages/Root/Root.tsx';
import { PageLoadingIndicator } from '@common/PageLoadingIndicator';
import { ProtectedRoute } from '@Auth/ProtectedRoute.tsx';
import { ThemeProviderWrapper } from '@styles/ThemeProviderWrapper.tsx';
import { AuthProvider } from '@Auth/AuthProvider.tsx';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { Dashboard } from '@pages/Dashboard/Dashboard.tsx';
import { Stages } from '@pages/Stocks/Stages/Stages.tsx';
import * as Sentry from '@sentry/react';

Sentry.init({
  dsn: 'https://45be92108625ab52496ba6c5a1b570e6@o4509045494448128.ingest.us.sentry.io/4509045495693312',
});

ModuleRegistry.registerModules([AllCommunityModule]);

// Lazy load components for better performance
const SignUp = lazy(() => import('@pages/SignUp/SignUp.tsx'));
const Home = lazy(() => import('@pages/Home/Home.tsx'));
const AllProducts = lazy(() => import('@pages/Home/AllProducts.tsx'));
const AllTags = lazy(() => import('@pages/Home/AllTags/AllTags.tsx'));
const ProductDetails = lazy(
  () => import('@pages/Home/ProductDetails/ProductDetails.tsx')
);
const AllBundles = lazy(() => import('@pages/Home/AllBundles.tsx'));
const BundleDetails = lazy(() => import('@pages/Home/BundleDetails.tsx'));
const Scanner = lazy(() => import('@pages/Scanner/Scanner.tsx'));
const Locations = lazy(() => import('@pages/Locations/Index.tsx'));
const AllLocations = lazy(() => import('@pages/Locations/AllLocations.tsx'));
const LocationDetails = lazy(
  () => import('@pages/Locations/LocationDetails/LocationDetails.tsx')
);
const Stocks = lazy(() => import('@pages/Stocks/index.tsx'));
const AllStocks = lazy(() => import('@pages/Stocks/AllStocks.tsx'));
const UserSettingsPage = lazy(
  () => import('@pages/UserSettings/UserSettings.tsx')
);
const UserPreferences = lazy(
  () => import('@pages/UserSettings/UserPreferences.tsx')
);
const OrganizationPreferences = lazy(
  () => import('@pages/UserSettings/OrganizationPreferences.tsx')
);

const PageNotFound = lazy(() => import('@pages/PageNotFound/PageNotFound.tsx'));

const AllTrades = lazy(() => import('@pages/Trades/AllTrades.tsx'));
const Trades = lazy(() => import('@pages/Trades/Trades.tsx'));

const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    children: [
      // Public routes
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'sign-up',
        element: (
          <Suspense fallback={<PageLoadingIndicator />}>
            <SignUp />
          </Suspense>
        ),
      },
      // Protected routes
      {
        path: '/',
        element: (
          <ProtectedRoute>
            <App />
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/products/all-products" replace />,
          },

          {
            path: 'scanner',
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <Scanner />
              </Suspense>
            ),
          },
          //Dashboard section
          {
            path: 'dashboard',
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <Dashboard />
              </Suspense>
            ),
          },
          // Products section
          {
            path: 'products',
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <Home />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="all-products" replace />,
              },
              {
                path: 'all-products',
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <AllProducts />
                  </Suspense>
                ),
              },
              {
                path: 'product/:id',
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <ProductDetails />
                  </Suspense>
                ),
              },
              {
                path: 'all-bundles',
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <AllBundles />
                  </Suspense>
                ),
              },
              {
                path: 'bundle/:id',
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <BundleDetails />
                  </Suspense>
                ),
              },
              {
                path: 'all-tags',
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <AllTags />
                  </Suspense>
                ),
              },
            ],
          },
          // Locations section
          {
            path: 'locations',
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <Locations />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="all-locations" replace />,
              },
              {
                path: 'location/:id',
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <LocationDetails />
                  </Suspense>
                ),
              },
              {
                path: 'all-locations',
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <AllLocations />
                  </Suspense>
                ),
              },
            ],
          },
          // Stocks section
          {
            path: 'stocks',
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <Stocks />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="all-stocks" replace />,
              },
              {
                path: 'stages',
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <Stages />
                  </Suspense>
                ),
              },
              {
                path: 'all-stocks',
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <AllStocks />
                  </Suspense>
                ),
              },
            ],
          },
          // Settings section
          {
            path: 'settings',
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <UserSettingsPage />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="user-preferences" replace />,
              },
              {
                path: 'user-preferences',
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <UserPreferences />
                  </Suspense>
                ),
              },
              {
                path: 'organization-preferences',
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <OrganizationPreferences />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'trades',
            element: (
              <Suspense fallback={<PageLoadingIndicator />}>
                <Trades />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="all-trades" replace />,
              },
              {
                path: 'all-trades',
                element: (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <AllTrades />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: (
      <Suspense fallback={<PageLoadingIndicator />}>
        <PageNotFound />
      </Suspense>
    ),
  },
]);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <AuthProvider>
    <ThemeProviderWrapper>
      <CssBaseline />
      <RouterProvider router={router} />
    </ThemeProviderWrapper>
  </AuthProvider>
);
